import React, { useCallback } from 'react';
import classnames from 'clsx';

import { Button } from 'ui-components/components/button';
import { InfoIcon } from 'ui-components/components/info-icon';
import { MDash } from 'ui-components/components/mdash';
import { Text, TextType } from 'ui-components/components/text';

import style from './new-rate-list-item.module.less';
import { DateService } from '../../../../services/date-service';
import DataDrivenLogo from '../../../data-driven-logo';
import { FormattedCurrency } from '../../../formatted-currency';
import { messages } from '../../messages';
import { RateTag } from '../../rate-tag';

const tagToMessageMap = {
  [RateTag.Recommended]: {
    tagMessage: messages.recommendedTag,
  },
  [RateTag.Cheapest]: {
    tagMessage: messages.cheapestTag,
    tooltipMessage: messages.cheapestTagTooltip,
  },
  [RateTag.Fastest]: {
    tagMessage: messages.fastestTag,
    tooltipMessage: messages.fastestTagTooltip,
  },
  [RateTag.BestValue]: {
    tagMessage: messages.bestValueTag,
    tooltipMessage: messages.bestValueTagTooltip,
  },
  [RateTag.TwoDay]: {
    tagMessage: messages.twoDayTag,
  },
};

/**
 * @typedef {import('../../../../prop-types/rate-browser-rate').RateBrowserRatePropType} RateBrowserRatePropType
 */

/**
 * @param {object} props
 * @param {RateBrowserRatePropType} props.rate - The rate object to display
 * @param {(rate: RateBrowserRatePropType) => void} props.onClick - Callback when rate is selected
 * @param {boolean} props.isSelected - Whether this rate is currently selected
 * @returns {React.ReactElement} The rendered rate list item component
 */
export const NewRateListItem = ({ rate, onClick, isSelected }) => {
  const { accountName, estimatedDeliveryTime, labelProviderId, packageCount, packageName, rate: innerRate, rateName, tag } = rate;

  const { value, code } = innerRate?.totalCost || { value: 0, code: 'USD' };

  const formattedDeliveryTime = DateService.formatDateFromRateDate(estimatedDeliveryTime);

  const selectRate = useCallback(() => {
    onClick(rate);
  }, [onClick, rate]);

  const tagMessages = tagToMessageMap[tag];
  const packageCaption = packageCount > 1 ? messages.multiPackageRateViewerCaption : packageName;

  return (
    <Button
      className={classnames(style['rate-list-item'], {
        [style['is-selected']]: isSelected,
        [style['has-tag']]: tag,
      })}
      onClick={selectRate}
      theme='unstyled'
    >
      {tagMessages?.tagMessage ? (
        <div
          className={classnames(style['rate-tag'], {
            [style['is-selected']]: isSelected,
          })}
        >
          <Text textType={TextType.Caption} value={tagMessages.tagMessage} />
          {tagMessages?.tooltipMessage ? (
            <InfoIcon
              color='white'
              position='top'
              size='small'
              tooltipText={<Text textType={TextType.Caption} value={tagMessages.tooltipMessage} />}
            />
          ) : null}
        </div>
      ) : null}
      <div className={style['provider-and-service']}>
        <DataDrivenLogo className={style['provider-logo']} logoType='icon' providerId={Number.parseInt(labelProviderId, 10)} />
        <div className={style['service-info']}>
          <div className={style['rate-name-and-tag']}>
            <Text textType={TextType.H4} value={rateName} />
          </div>
          <div className={style['package-and-account']}>
            <Text className={style['account-name']} textType={TextType.Caption} title={accountName} value={accountName || <MDash />} />
            <Text
              className={style['package-name']}
              formattedMessageValues={{ count: packageCount }}
              textType={TextType.Caption}
              title={packageName}
              value={packageCaption || <MDash />}
            />
          </div>
        </div>
      </div>
      <Text className={style['delivery-time']} textType={TextType.H4} value={formattedDeliveryTime || <MDash />} />
      <Text className={style['rate-value']} textType={TextType.H4} value={<FormattedCurrency currencyCode={code} value={value} />} />
    </Button>
  );
};
